import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Instagram, Twitter, Linkedin, ArrowUpRight, Loader2, MessageCircle } from "lucide-react";
import { Logo } from "./Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Footer = ({ onNav }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("Enter a valid email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("You're on the list!", { description: "Print tips & vendor stories, once a month." });
      setEmail("");
    } catch {
      toast.error("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="relative bg-[#0D0B14] text-[#FAF7F2] pt-20 pb-10 overflow-hidden grain" data-testid="site-footer">
      <div className="absolute -top-24 left-1/4 w-96 h-96 rounded-full bg-[#FF5A36]/10 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-10 items-center pb-14 border-b border-white/10">
          <div>
            <h3 className="font-display font-extrabold text-2xl sm:text-3xl" data-testid="newsletter-headline">
              Print tips & vendor stories. <span className="text-[#FBBF24]">Monthly.</span>
            </h3>
            <p className="text-[#FAF7F2]/60 mt-2 text-sm">No spam — just ideas for brands that fit in one hand.</p>
          </div>
          <form onSubmit={subscribe} className="flex gap-2" data-testid="newsletter-form">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yourbrand.com"
              className="flex-1 bg-white/5 border border-white/15 focus:border-[#FBBF24] rounded-full px-5 py-3.5 text-sm font-medium placeholder:text-white/30 outline-none transition-colors duration-300"
              data-testid="newsletter-email-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="shrink-0 flex items-center gap-2 font-bold text-sm bg-[#FF5A36] hover:bg-[#FBBF24] hover:text-[#2E1065] rounded-full px-6 py-3.5 transition-all duration-300 disabled:opacity-60"
              data-testid="newsletter-subscribe-button"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Join"}
            </button>
          </form>
        </div>

        <div className="grid sm:grid-cols-3 gap-10 py-12">
          <div>
            <div className="mb-4">
              <Logo dark className="h-14" />
            </div>
            <p className="text-sm text-[#FAF7F2]/55 leading-relaxed max-w-xs">
              Branded tea & coffee cups connecting India's chai tapris, tea
              stalls and cafés with neighbourhood brands. Advertising you can hold.
            </p>
            <p className="mt-4 flex items-center gap-2 text-sm font-bold text-[#FBBF24]" data-testid="footer-email">
              <MessageCircle className="w-4 h-4" /> enquiry@cupconnect.net
            </p>
            <p className="mt-1 text-xs text-[#FAF7F2]/40" data-testid="footer-domain">cupconnect.net</p>
            <div className="flex gap-3 mt-5">
              {[Instagram, Twitter, Linkedin].map((Icon, i) => (
                <button
                  key={i}
                  className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-[#FF5A36] hover:border-[#FF5A36] transition-all duration-300"
                  aria-label="Social link"
                  data-testid={`footer-social-${i}`}
                >
                  <Icon className="w-4.5 h-4.5 w-5 h-5" />
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-black uppercase tracking-[0.25em] text-[#FAF7F2]/40 mb-5">Explore</p>
            <ul className="space-y-3">
              {[["Products", "#products"], ["Ad Reach Calculator", "#reach"], ["Manufacturing", "#manufacturing"], ["Sustainability", "#eco"], ["About Us", "/about"], ["Privacy Policy", "/privacy"], ["Get a Quote", "#quote"]].map(([label, href]) => (
                <li key={href}>
                  <button
                    onClick={() => onNav(href)}
                    className="group flex items-center gap-1.5 text-sm font-semibold text-[#FAF7F2]/70 hover:text-[#FBBF24] transition-colors duration-300"
                    data-testid={`footer-link-${label.toLowerCase().replace(/ /g, "-")}`}
                  >
                    {label}
                    <ArrowUpRight className="w-3.5 h-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-xs font-black uppercase tracking-[0.25em] text-[#FAF7F2]/40 mb-5">Certified</p>
            <div className="flex flex-wrap gap-2">
              {["FSC®", "EN 13430", "EN 13432", "Food-Safe Inks"].map((c) => (
                <span key={c} className="text-xs font-bold bg-white/5 border border-white/10 rounded-full px-3.5 py-2 text-[#FAF7F2]/70">
                  {c}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="select-none text-center py-6" data-testid="footer-giant-logo">
          <p className="font-blackout text-[13vw] leading-none text-stroke-cream hover:text-[#FF5A36] transition-colors duration-700 cursor-default">
            CUP CONNECT
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 pt-6 border-t border-white/10 text-xs text-[#FAF7F2]/40">
          <p>© 2026 Cup Connect. All rights reserved.</p>
          <button
            onClick={() => onNav("/privacy")}
            className="font-bold hover:text-[#FBBF24] transition-colors duration-300"
            data-testid="footer-privacy-link"
          >
            Privacy Policy
          </button>
          <p>Made for brands that fit in one hand.</p>
        </div>
      </div>
    </footer>
  );
};
